'use client';
import { Box, ButtonBase, Link, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../constants';
import { MaterialIcon } from '../materialIcon';

/**
 * Carrousel component displays a horizontal scrolling list of items with optional scroll buttons.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {any[]} props.items - Array of items to be displayed in the carousel.
 * @param {string} props.title - Title of the carousel.
 * @param {string} props.seeMoreLink - URL for the "See More" link.
 * @param {React.ReactNode} props.children - The child nodes to be rendered inside the carousel.
 *
 * @returns {React.JSX.Element} The rendered Carrousel component.
 *
 * @example
 * <Carrousel items={items} title="Top Rated" seeMoreLink="/see-more">
 *   {items.map(item => (
 *     <Card key={item.id} item={item} />
 *   ))}
 * </Carrousel>
 */

interface CarouselProps {
  title: string;
  seeMoreLink: string;
  children: React.ReactNode;
}

export const Carousel: React.FC<CarouselProps> = ({ title, seeMoreLink, children }) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);
  const totalChildren = React.Children.count(children);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(totalChildren > 2);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Scroll to the left
  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    }
  };

  // Scroll to the right
  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(newIndex);
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const calculateCardWidth = () => {
    if (carouselRef.current && carouselRef.current.firstChild) {
      const firstCard = carouselRef.current.firstChild as HTMLElement;
      setCardWidth(firstCard.clientWidth + 32);
    }
  };

  useEffect(() => {
    // Set card width and recalculate on resize
    calculateCardWidth();
    window.addEventListener('resize', calculateCardWidth);

    // Add scroll event listener to update button visibility and current index
    if (carouselRef.current) {
      carouselRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('resize', calculateCardWidth);
      if (carouselRef.current) {
        carouselRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [children, cardWidth]);

  return (
    <Stack
      data-testid="carrousel"
      sx={{
        gap: 1,
        mt: 1,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'space-between',
        }}
      >
        <Typography component="h2" variant="h1">
          {title}
        </Typography>
        <Link href={seeMoreLink}>
          <Typography variant="body1" color={COLORS.brand.main}>
            Ver más
          </Typography>
        </Link>
      </Box>

      {/* Scroll buttons */}
      <>
        {canScrollLeft && (
          <ButtonBase
            onClick={scrollLeft}
            aria-label="Anterior"
            data-testid="carrousel-scroll-left"
            sx={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white',
              position: 'absolute',
              left: 15,
              top: '50%',
              zIndex: 1,
              backgroundColor: COLORS.text.light3,
              cursor: 'pointer',
            }}
          >
            <MaterialIcon icon="chevron_left" size={20} aria-hidden="true" />
          </ButtonBase>
        )}

        {canScrollRight && (
          <ButtonBase
            onClick={scrollRight}
            aria-label="Siguiente"
            data-testid="carrousel-scroll-right"
            sx={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white',
              position: 'absolute',
              right: 15,
              top: '50%',
              zIndex: 1,
              backgroundColor: COLORS.text.light3,
              cursor: 'pointer',
            }}
          >
            <MaterialIcon icon="chevron_right" size={20} aria-hidden="true" />
          </ButtonBase>
        )}
      </>

      {/* Scrollable container */}
      <Box
        ref={carouselRef}
        onScroll={handleScroll}
        data-testid="carrousel-scroll-container"
        sx={{
          borderRadius: 2,
          gap: 2,
          p: { xs: 2, md: 0 },
          display: 'flex',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollSnapType: 'x mandatory',
        }}
      >
        {children}
      </Box>

      {/* Mobile Index Indicator */}
      <Stack
        direction="row"
        spacing={1}
        data-testid="carrousel-indicators"
        role="img"
        aria-label={`Página ${currentIndex + 1} de ${totalChildren}`}
        sx={{
          justifyContent: 'center',
          mt: 1,
          display: { xs: 'flex', md: 'none' },
        }}
      >
        {Array.from({ length: totalChildren }).map((_, index) => (
          <Box
            key={index}
            sx={{
              width: 20,
              height: 10,
              borderRadius: 2,
              backgroundColor: index === currentIndex ? COLORS.brand.main : '#C4C4C4',
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
};
